import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/Header/Header';
import NavInf from '../../components/NavInf/NavInf';
import Footer from '../../components/Footer/Footer';
import Loader from '../../components/Loader/Loader';
import './Points.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faRocket, faGem, faCrown } from '@fortawesome/free-solid-svg-icons';
import secureAxios from '../../utils/secureAxios';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';

const Points = () => {
    const [currentPoints, setCurrentPoints] = useState(0);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [pointPackages, setPointPackages] = useState([]);
    const [error, setError] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);

    // Iconos para los paquetes (basado en el ID)
    const getPackageIcon = (packageId) => {
        const icons = {
            1: faStar,
            2: faRocket, 
            3: faGem,
            4: faCrown
        };
        return icons[packageId] || faStar;
    };

    // Cargar puntos actuales del usuario
    const loadUserPoints = useCallback(async () => {
        try {
            setError(null);
            
            const response = await secureAxios.get('/points/balance');
            console.log('Respuesta del balance de puntos:', response.data);
            
            if (response.data && typeof response.data.points === 'number') {
                setCurrentPoints(response.data.points);
            } else {
                setCurrentPoints(0);
            }
        } catch (error) {
            console.error('Error al cargar puntos:', error);
            setError('Error al cargar el saldo de puntos');
            setCurrentPoints(0);
        }
    }, []);

    // Cargar paquetes disponibles
    const loadPointPackages = useCallback(async () => {
        try {
            const response = await secureAxios.get('/points/packages');
            console.log('Respuesta de paquetes:', response.data);
            
            if (response.data && Array.isArray(response.data)) {
                // Mapear los paquetes del backend al formato del frontend
                const mappedPackages = response.data.map(pkg => ({
                    id: pkg.id,
                    points: pkg.points,
                    price: pkg.price,
                    bonus: pkg.bonus || 0,
                    totalPoints: pkg.totalPoints,
                    popular: pkg.popular || false,
                    color: pkg.color, // Usar el color que viene del backend
                    icon: getPackageIcon(pkg.id)
                    // wompiPaymentUrl ya no es necesario - se genera dinámicamente
                }));
                
                setPointPackages(mappedPackages);
            } else {
                console.warn('Formato de respuesta inesperado:', response.data);
                setPointPackages([]);
            }
        } catch (error) {
            console.error('Error al cargar paquetes:', error);
            setError('Error al cargar los paquetes de puntos');
            setPointPackages([]);
        }
    }, []);

    // Función para cargar todos los datos
    const loadAllData = useCallback(async () => {
        setIsLoading(true);
        setDataLoaded(false);
        try {
            await Promise.all([
                loadUserPoints(),
                loadPointPackages()
            ]);
            setDataLoaded(true);
        } catch (error) {
            console.error('Error al cargar datos:', error);
            setError('Error al cargar los datos');
        } finally {
            setIsLoading(false);
        }
    }, [loadUserPoints, loadPointPackages]);

    // Efecto para escuchar cambios de autenticación
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                // Usuario autenticado, cargar datos
                setIsAuthenticated(true);
                loadAllData();
            } else {
                // Usuario no autenticado
                setIsAuthenticated(false);
                setIsLoading(false);
                setDataLoaded(false);
                setError('Debes iniciar sesión para ver tus puntos');
                setCurrentPoints(0);
                setPointPackages([]);
            }
        });

        // Cleanup function
        return () => unsubscribe();
    }, [loadAllData]);

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0
        }).format(price);
    };

    const handlePurchase = useCallback(async (packageData) => {
        try {
            setSelectedPackage(packageData);
            console.log('Iniciando compra del paquete:', packageData);
            
            // Solicitar payment link dinámico al backend
            const response = await secureAxios.get(`/points/payment-link/${packageData.id}`);
            
            if (response.data && response.data.paymentLinkId) {
                const { pointsToReceive, paymentLinkId } = response.data;
                
                // Construir la URL completa de Wompi
                const paymentUrl = `https://checkout.wompi.co/l/${paymentLinkId}`;
                
                // Mostrar mensaje de confirmación
                const confirmMessage = `¿Confirmas la compra de ${pointsToReceive.toLocaleString()} puntos por ${formatPrice(packageData.price)}?`;
                
                if (window.confirm(confirmMessage)) {
                    // Redirigir a Wompi
                    window.location.href = paymentUrl;
                } else {
                    setSelectedPackage(null);
                }
            } else {
                throw new Error('No se pudo generar el enlace de pago');
            }
        } catch (error) {
            console.error('Error al procesar la compra:', error);
            alert('Error al procesar la compra. Por favor, inténtalo de nuevo.');
            setSelectedPackage(null);
        }
    }, []);

    return (
        <>
            <Header />
            
            {/* Mostrar Loader mientras carga o no está autenticado */}
            {(isLoading || !isAuthenticated || !dataLoaded) ? (
                <Loader />
            ) : (
                <div className="points-page-container">
                    {error && (
                        <div className="points-error-message">
                            {error}
                        </div>
                    )}
                    
                    <div className="points-page-header">
                        <div className="points-current-balance">
                            <div className="points-balance-card">
                                <div className="points-balance-info">
                                    <h2 className="points-balance-title">Tus puntos actuales</h2>
                                    <div className="points-balance-amount">
                                        {currentPoints.toLocaleString()}
                                    </div>
                                    <p className="points-balance-subtitle">
                                        Puntos disponibles para usar
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="points-page-title-section">
                            <h1 className="points-page-title">Compra paquetes de puntos</h1>
                            <p className="points-page-subtitle">
                                Potencia tu negocio con nuestros paquetes de puntos. 
                                ¡Entre más compres, más puntos bonus recibes!
                            </p>
                        </div>
                    </div>

                    <div className="points-packages-grid">
                        {pointPackages.length === 0 ? (
                            <div className="points-no-packages">
                                <p>No hay paquetes disponibles en este momento.</p>
                            </div>
                        ) : (
                            pointPackages.map((pkg) => (
                                <div 
                                    key={pkg.id} 
                                    className={`points-package-card ${pkg.popular ? 'points-package-popular' : ''}`}
                                    style={{ '--package-color': pkg.color }}
                                >
                                    {pkg.popular && (
                                        <div className="points-package-popular-badge">
                                            MÁS POPULAR
                                        </div>
                                    )}
                                    
                                    <div className="points-package-icon">
                                        <FontAwesomeIcon icon={pkg.icon} />
                                    </div>
                                    
                                    <div className="points-package-content">
                                        <h3 className="points-package-title">
                                            {pkg.points.toLocaleString()} Puntos
                                        </h3>
                                        
                                        {pkg.bonus > 0 && (
                                            <div className="points-package-bonus">
                                                <span className="points-bonus-text">
                                                    + {pkg.bonus} puntos BONUS
                                                </span>
                                                <div className="points-total-display">
                                                    = {(pkg.totalPoints || (pkg.points + pkg.bonus)).toLocaleString()} puntos totales
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div className="points-package-price">
                                            <span className="points-price-amount">
                                                {formatPrice(pkg.price)} USD
                                            </span>
                                        </div>
                                        
                                        <button 
                                            className="points-package-buy-btn"
                                            onClick={() => handlePurchase(pkg)}
                                            disabled={selectedPackage?.id === pkg.id}
                                        >
                                            <span className="points-buy-btn-text">
                                                {selectedPackage?.id === pkg.id ? 'Procesando...' : 'Comprar ahora'}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="points-info-section">
                        <div className="points-benefits-card">
                            <h3 className="points-benefits-title">¿Por qué comprar puntos?</h3>
                            <div className="points-benefits-grid">
                                <div className="points-benefit-item">
                                    <div className="points-benefit-text">
                                        <strong>Mejora tu visibilidad</strong>
                                        <p>Destaca tus productos en las búsquedas</p>
                                    </div>
                                </div>
                                <div className="points-benefit-item">
                                    <div className="points-benefit-text">
                                        <strong>Aumenta tus ventas</strong>
                                        <p>Llega a más clientes potenciales</p>
                                    </div>
                                </div>
                                <div className="points-benefit-item">
                                    <div className="points-benefit-text">
                                        <strong>Acceso premium</strong>
                                        <p>Funciones exclusivas para proveedores</p>
                                    </div>
                                </div>
                                <div className="points-benefit-item">
                                    <div className="points-benefit-text">
                                        <strong>Puntos bonus</strong>
                                        <p>Más compras = más puntos gratis</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Solo mostrar NavInf y Footer cuando no está cargando */}
            {(!isLoading && isAuthenticated && dataLoaded) && (
                <>
                    <NavInf />
                    <Footer navinf={true} />
                </>
            )}
        </>
    );
};


export default Points;